import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Leaf, Factory, Hammer, CalendarDays, Globe,
  ArrowUpRight, ChevronDown, MapPin, Users, Flag, Clock,
  Mic2, Sparkles, Handshake, LifeBuoy, Mail, ExternalLink, Mountain, Lock,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Recent-activity helpers (used by the "Last to register" indicator) ─────
const ccToFlag = (cc) => {
  if (!cc || typeof cc !== 'string' || cc.length !== 2) return '';
  return String.fromCodePoint(
    ...cc.toUpperCase().split('').map(c => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
};

const relativeTime = (iso, tr) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60)        return tr ? 'az önce'        : 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60)        return tr ? `${min} dk önce` : `${min}m ago`;
  const hr  = Math.floor(min / 60);
  if (hr  < 24)        return tr ? `${hr} sa önce`  : `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30)        return tr ? `${day} gün önce`: `${day}d ago`;
  const mo  = Math.floor(day / 30);
  return                       tr ? `${mo} ay önce` : `${mo}mo ago`;
};

// ── SummitFlow 2026 opening moment (Istanbul time, UTC+3) ────────────────────
const SUMMIT_OPENING_ISO = '2026-10-14T09:00:00+03:00';

// ── Countdown hook ───────────────────────────────────────────────────────────
const useCountdown = (targetIso) => {
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours   = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds, done: diff === 0 };
};

// ── Fade-up reveal on scroll ─────────────────────────────────────────────────
const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, setRef] = useState(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } },
      { threshold: 0.15 }
    );
    io.observe(ref);
    return () => io.disconnect();
  }, [ref]);
  return (
    <div
      ref={setRef}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-1000 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
    >
      {children}
    </div>
  );
};

// ── Countdown tile ───────────────────────────────────────────────────────────
const TimeTile = ({ value, label, 'data-testid': testid }) => (
  <div data-testid={testid} className="flex flex-col items-center">
    <span className="font-playfair text-4xl sm:text-5xl md:text-6xl text-[#0A3A5A] tabular-nums leading-none">
      {String(value).padStart(2, '0')}
    </span>
    <span className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#4A5568] font-semibold">
      {label}
    </span>
  </div>
);

// ── Zone card ────────────────────────────────────────────────────────────────
const ZoneCard = ({
  testId, tone, title, subtitle, description, Icon, href, ctaLabel, span = 'lg:col-span-6',
  disabled = false, comingSoonLabel,
}) => {
  const palettes = {
    blue: {
      bg: 'bg-[#0A3A5A]',        text: 'text-white',   muted: 'text-blue-100/80',
      badge: 'bg-white/10 text-white border-white/20',
      cta: 'text-white',         hoverShadow: 'hover:shadow-[0_25px_45px_-20px_rgba(10,58,90,0.55)]',
      iconWrap: 'bg-white/10 border-white/20 text-white',
    },
    green: {
      bg: 'bg-[#216E4E]',        text: 'text-white',   muted: 'text-emerald-50/80',
      badge: 'bg-white/10 text-white border-white/20',
      cta: 'text-white',         hoverShadow: 'hover:shadow-[0_25px_45px_-20px_rgba(33,110,78,0.55)]',
      iconWrap: 'bg-white/10 border-white/20 text-white',
    },
    amber: {
      bg: 'bg-white',            text: 'text-[#0A3A5A]', muted: 'text-[#4A5568]',
      badge: 'bg-[#FEF3E8] text-[#A94D32] border-[#F4D4B5]',
      cta: 'text-[#A94D32]',     hoverShadow: 'hover:shadow-[0_25px_45px_-20px_rgba(217,119,54,0.35)]',
      iconWrap: 'bg-[#FEF3E8] border-[#F4D4B5] text-[#A94D32]',
    },
    terracotta: {
      bg: 'bg-white',            text: 'text-[#0A3A5A]', muted: 'text-[#4A5568]',
      badge: 'bg-[#FDECE7] text-[#A94D32] border-[#F2C8BB]',
      cta: 'text-[#A94D32]',     hoverShadow: 'hover:shadow-[0_25px_45px_-20px_rgba(169,77,50,0.35)]',
      iconWrap: 'bg-[#FDECE7] border-[#F2C8BB] text-[#A94D32]',
    },
  };
  const p = palettes[tone];

  const inner = (
    <>
      <div>
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className={`h-12 w-12 rounded-xl border flex items-center justify-center ${p.iconWrap}`}>
            <Icon className="h-6 w-6" strokeWidth={1.6} />
          </div>
          <span className={`text-[10px] uppercase tracking-[0.25em] font-semibold px-2.5 py-1 rounded-full border ${p.badge}`}>
            {subtitle}
          </span>
        </div>
        <h3 className={`font-playfair text-2xl md:text-3xl leading-tight mb-3 ${p.text}`}>
          {title}
        </h3>
        <p className={`text-sm md:text-[15px] leading-relaxed ${p.muted} max-w-md`}>
          {description}
        </p>
      </div>
      {disabled ? (
        <div className={`mt-8 inline-flex items-center gap-2 text-sm font-semibold ${p.cta} opacity-90`}>
          <Lock className="h-4 w-4" />
          {comingSoonLabel}
        </div>
      ) : (
        <div className={`mt-8 inline-flex items-center gap-2 text-sm font-semibold ${p.cta} group-hover:gap-3 transition-all`}>
          {ctaLabel}
          <ArrowUpRight className="h-4 w-4" />
        </div>
      )}
    </>
  );

  const baseClasses = `relative col-span-12 md:col-span-6 ${span} ${p.bg} ${p.text} border border-[rgba(10,58,90,0.08)] rounded-2xl p-7 md:p-9 flex flex-col justify-between`;

  if (disabled) {
    return (
      <div
        data-testid={testId}
        aria-disabled="true"
        className={`${baseClasses} opacity-60 grayscale-[0.25] cursor-not-allowed select-none`}
      >
        {inner}
      </div>
    );
  }

  return (
    <Link
      to={href}
      data-testid={testId}
      className={`group ${baseClasses} transition-all duration-500 hover:-translate-y-1 ${p.hoverShadow}`}
    >
      {inner}
    </Link>
  );
};

// ── FAQ accordion item ───────────────────────────────────────────────────────
const FaqRow = ({ q, a, open, onToggle, testId }) => (
  <div data-testid={testId} className="border-b border-[rgba(10,58,90,0.12)]">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-6 py-6 text-left"
    >
      <span className="font-playfair text-lg md:text-xl text-[#0A3A5A] leading-tight">{q}</span>
      <ChevronDown className={`flex-shrink-0 h-5 w-5 text-[#4A5568] transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
    </button>
    <div className={`grid transition-all duration-500 ease-out ${open ? 'grid-rows-[1fr] opacity-100 pb-6' : 'grid-rows-[0fr] opacity-0'}`}>
      <div className="overflow-hidden text-[#4A5568] text-[15px] leading-relaxed max-w-3xl">
        {a}
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

export default function HomePage() {
  const { language, localizedPath } = useLanguage();
  const tr = language === 'tr';
  const { days, hours, minutes, seconds } = useCountdown(SUMMIT_OPENING_ISO);

  const [stats, setStats]       = useState(null);
  const [statsLoading, setLoad] = useState(true);
  const [statsError, setError]  = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/cop-stats`, { timeout: 6000 })
      .then(res => {
        if (cancelled) return;
        const d = res?.data && typeof res.data === 'object' ? res.data : null;
        setStats(d);
        setLoad(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setStats({
          delegates_count: 0, countries_count: 0,
          registered_total: 0, suppliers_count: 0,
        });
        setLoad(false);
      });
    return () => { cancelled = true; };
  }, []);

  const [faqOpen, setFaqOpen] = useState(0);

  const faqs = [
    {
      q: tr ? 'SummitFlow nedir?' : 'What is SummitFlow?',
      a: tr
        ? 'SummitFlow, dünyanın önde gelen liderlerini, kurumlarını ve iş dünyasını tek bir güvenli platformda buluşturan küresel zirve ve etkinlik yönetim portalıdır. Kayıt, katılım, tedarik ve stand yönetimini uçtan uca yürütür.'
        : 'SummitFlow is a global summit and event-management portal that brings the world\'s leading minds, institutions and businesses together in one secure platform — handling registration, participation, sourcing and stand management end-to-end.',
    },
    {
      q: tr ? 'Blue Zone ile Green Zone arasındaki fark nedir?' : 'What is the difference between Blue Zone and Green Zone?',
      a: tr
        ? 'Blue Zone, akredite delegeler için ayrılmış resmi katılım alanıdır. Green Zone ise iş dünyası, medya ve genel katılımcılara açık kamuya yönelik programdır.'
        : 'The Blue Zone is the accredited space reserved for official delegates. The Green Zone is the public-facing programme open to business, media and general attendees.',
    },
    {
      q: tr ? 'Blue Zone için nasıl kayıt olabilirim?' : 'How do I register for the Blue Zone?',
      a: tr
        ? 'Kayıtlı delegeyseniz e-posta doğrulama akışıyla giriş yapın. Yeni başvuru yapacak kurumlar kayıt formunu doldurup organizasyon onayını bekler.'
        : 'If you are a registered delegate, sign in via the email verification flow. New institutional applications complete the form and await organiser approval.',
    },
    {
      q: tr ? 'Tedarikçi veya yüklenici olarak nasıl katılırım?' : 'How can I participate as a supplier or contractor?',
      a: tr
        ? 'Tedarikçiler B2B kataloğa ürün yükler, yükleniciler stand tasarım ihalelerine teklif verir. Her iki kayıt akışı da bu sayfadaki ilgili kartlardan başlar.'
        : 'Suppliers list products in the B2B catalogue; contractors bid on stand-design tenders. Both journeys begin from the cards above.',
    },
    {
      q: tr ? 'Katılım ücretli midir?' : 'Is participation paid?',
      a: tr
        ? 'Delege kaydı ücretsizdir. Tedarikçi ve yüklenici katılımı, etkinlik protokolü ve kategori üyeliği kurallarına tabidir.'
        : 'Delegate registration is free. Supplier and contractor participation is subject to the event protocol and category membership rules.',
    },
    {
      q: tr ? 'Programı nereden görebilirim?' : 'Where can I see the programme?',
      a: tr
        ? 'Resmi program ve oturumlar için yukarıdaki "Program" bölümünü inceleyebilirsiniz.'
        : 'Visit the "Agenda" section above for the official schedule and sessions.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A202C] font-outfit overflow-x-hidden">
      <Navbar />

      {/* ═════════════════════════ HERO ═════════════════════════ */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* LEFT */}
            <div className="lg:col-span-7">
              <Reveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[rgba(10,58,90,0.12)] text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0A3A5A] mb-6">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#216E4E] animate-pulse" />
                  {tr ? 'Kayıtlar Açık' : 'Registrations Open'}
                </div>
              </Reveal>
              <Reveal delay={80}>
                <h1
                  data-testid="hero-title"
                  className="font-playfair text-5xl md:text-6xl lg:text-7xl text-[#0A3A5A] leading-[1.05] tracking-tight font-medium"
                >
                  {tr ? (
                    <>Küresel Liderlik ve İnovasyon <span className="italic text-[#216E4E]">Zirvesi</span></>
                  ) : (
                    <>The Global Leadership & Innovation <span className="italic text-[#216E4E]">Summit</span></>
                  )}
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[#4A5568]">
                  <span className="inline-flex items-center gap-2 text-base md:text-lg">
                    <CalendarDays className="h-4 w-4 text-[#0A3A5A]" />
                    <span className="font-semibold text-[#0A3A5A]">{tr ? '14–16 Ekim 2026' : '14–16 October 2026'}</span>
                  </span>
                  <span className="h-4 w-px bg-[#4A5568]/30" />
                  <span className="inline-flex items-center gap-2 text-base md:text-lg">
                    <MapPin className="h-4 w-4 text-[#0A3A5A]" />
                    {tr ? 'İstanbul, Türkiye' : 'Istanbul, Türkiye'}
                  </span>
                </div>
              </Reveal>
              <Reveal delay={220}>
                <p className="mt-6 max-w-xl text-[#4A5568] text-base md:text-lg leading-relaxed">
                  {tr
                    ? 'Dünyanın önde gelen liderlerini, kurumlarını ve fikirlerini tek bir çatı altında buluşturan platform. Delegeler, iş dünyası ve tedarikçiler için tek ve güvenli portal.'
                    : 'The platform bringing the world\'s leading minds, institutions and ideas together under one roof — one secure portal for delegates, business and suppliers.'}
                </p>
              </Reveal>

              {/* Countdown */}
              <Reveal delay={280}>
                <div data-testid="hero-countdown" className="mt-10 inline-flex items-stretch rounded-2xl backdrop-blur-xl bg-white/80 border border-[rgba(10,58,90,0.10)] px-6 py-5 gap-6 md:gap-10 shadow-[0_8px_30px_-10px_rgba(10,58,90,0.15)]">
                  <div className="flex flex-col justify-between pr-4 md:pr-6 border-r border-[rgba(10,58,90,0.12)]">
                    <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A5568]">
                      {tr ? 'Açılışa kalan' : 'Opens in'}
                    </span>
                    <span className="mt-1 font-playfair text-xs italic text-[#0A3A5A]/70">
                      {tr ? 'İstanbul, 09.00' : 'Istanbul, 09:00'}
                    </span>
                  </div>
                  <TimeTile value={days}    label={tr ? 'Gün'    : 'Days'}    testid="cd-days" />
                  <TimeTile value={hours}   label={tr ? 'Saat'   : 'Hours'}   testid="cd-hours" />
                  <TimeTile value={minutes} label={tr ? 'Dakika' : 'Minutes'} testid="cd-minutes" />
                  <TimeTile value={seconds} label={tr ? 'Saniye' : 'Seconds'} testid="cd-seconds" />
                </div>
              </Reveal>

              {/* Primary CTAs — access paths not yet open (non-clickable) */}
              <Reveal delay={360}>
                <div className="mt-10 flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled
                    data-testid="hero-cta-blue"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#0A3A5A] text-white text-sm font-semibold opacity-60 cursor-not-allowed"
                  >
                    <Lock className="h-4 w-4" strokeWidth={1.8} />
                    {tr ? 'Delegate Access · Yakında' : 'Delegate Access · Coming soon'}
                  </button>
                  <button
                    type="button"
                    disabled
                    data-testid="hero-cta-green"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#216E4E] text-white text-sm font-semibold opacity-60 cursor-not-allowed"
                  >
                    <Lock className="h-4 w-4" strokeWidth={1.8} />
                    {tr ? 'Attendee Access · Yakında' : 'Attendee Access · Coming soon'}
                  </button>
                </div>
              </Reveal>
            </div>

            {/* RIGHT — collage */}
            <div className="lg:col-span-5">
              <Reveal delay={200}>
                <div className="relative aspect-[4/5] max-w-md mx-auto">
                  <div className="absolute inset-0 rounded-[28px] overflow-hidden shadow-[0_30px_60px_-30px_rgba(10,58,90,0.45)]">
                    <img
                      src="https://images.pexels.com/photos/9275222/pexels-photo-9275222.jpeg"
                      alt="Summit audience"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A3A5A]/40 via-transparent to-transparent" />
                  </div>
                  <div className="absolute -bottom-8 -left-6 w-40 md:w-48 aspect-square rounded-2xl overflow-hidden border-[6px] border-[#FAF9F6] shadow-[0_20px_50px_-20px_rgba(33,110,78,0.4)] rotate-[-4deg]">
                    <img
                      src="https://images.pexels.com/photos/6285086/pexels-photo-6285086.jpeg"
                      alt="Business networking"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -top-4 -right-4 rounded-2xl bg-white/90 backdrop-blur-xl border border-[rgba(10,58,90,0.12)] px-5 py-4 shadow-[0_15px_35px_-15px_rgba(10,58,90,0.35)]">
                    <div className="flex items-center gap-2 text-[#216E4E]">
                      <Sparkles className="h-4 w-4" />
                      <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">SummitFlow</span>
                    </div>
                    <p className="mt-1 font-playfair text-base text-[#0A3A5A] leading-tight">
                      {tr ? 'Fikirlerin Buluştuğu Yer' : 'Where Ideas Convene'}
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═════════════════════════ ZONE CARDS (Bento) ═════════════════════════ */}
      <section id="zones" className="py-16 md:py-24 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="max-w-3xl mb-12">
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A94D32]">
                {tr ? 'Katılım Yolları' : 'Ways to Participate'}
              </span>
              <h2 className="mt-3 font-playfair text-3xl md:text-5xl leading-tight text-[#0A3A5A] font-medium">
                {tr ? 'Rolünüzü seçin. Zirveye katılın.' : 'Choose your role. Join the summit.'}
              </h2>
              <p className="mt-4 text-[#4A5568] text-base md:text-lg">
                {tr
                  ? 'Dört farklı akış; tek, güvenli portal. Her rol için net erişim ve doğrulama kuralları.'
                  : 'Four distinct journeys, one secure portal — with clear access and verification rules for each role.'}
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="grid grid-cols-12 gap-6 md:gap-8">
              <ZoneCard
                testId="zone-blue-card"
                tone="blue"
                Icon={Shield}
                subtitle={tr ? 'Delege' : 'Delegate'}
                title="Delegate Access"
                description={tr
                  ? 'Akredite delegeler için resmi katılım alanı. E-posta doğrulaması ile güvenli giriş.'
                  : 'The accredited participation space for official delegates. Secure access via email verification.'}
                disabled
                comingSoonLabel={tr ? 'Yakında' : 'Coming soon'}
                span="lg:col-span-6"
              />
              <ZoneCard
                testId="zone-green-card"
                tone="green"
                Icon={Leaf}
                subtitle={tr ? 'Katılımcı' : 'Attendee'}
                title="Attendee Access"
                description={tr
                  ? 'İş dünyası, medya ve genel katılımcılara açık program. Başvurular organizasyon onayından geçer.'
                  : 'Public-facing programme for business, media and attendees. Applications are reviewed by the organiser.'}
                disabled
                comingSoonLabel={tr ? 'Yakında' : 'Coming soon'}
                span="lg:col-span-6"
              />
              <ZoneCard
                testId="zone-supplier-card"
                tone="amber"
                Icon={Factory}
                subtitle={tr ? 'Tedarikçi' : 'Supplier'}
                title={tr ? 'Tedarikçi Pazarı' : 'Supplier Marketplace'}
                description={tr
                  ? 'B2B kataloğuna ürün yükle, teklif al, etkinlik öncesi ve sırasında anlaşmaları yönet.'
                  : 'Publish your B2B catalogue, receive RFQs and manage deals before and during the event.'}
                ctaLabel={tr ? 'Tedarikçi Kaydı' : 'Register as Supplier'}
                href={localizedPath('supplierRegister')}
                span="lg:col-span-6"
              />
              <ZoneCard
                testId="zone-contractor-card"
                tone="terracotta"
                Icon={Hammer}
                subtitle={tr ? 'Yüklenici' : 'Contractor'}
                title={tr ? 'Stand Tasarım İhalesi' : 'Stand Design Bids'}
                description={tr
                  ? 'Stand tasarım çağrılarına teklif ver, portföyünü yayınla, müşterilerle doğrudan mesajlaş.'
                  : 'Bid on stand-design briefs, publish your portfolio and message clients directly.'}
                ctaLabel={tr ? 'Yüklenici Kaydı' : 'Register as Contractor'}
                href={localizedPath('contractorRegister')}
                span="lg:col-span-6"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ LIVE STATS STRIP ═════════════════════════ */}
      <section data-testid="stats-strip" className="relative">
        <div className="absolute inset-0 bg-[#0A3A5A]" />
        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_20%_30%,#fff_0,transparent_45%),radial-gradient(circle_at_80%_70%,#6EE7B7_0,transparent_35%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <Reveal>
            <div className="mb-10 md:mb-14 max-w-2xl">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6EE7B7]">
                  {tr ? 'Canlı Katılım' : 'Live Participation'}
                </span>
                <span
                  data-testid="stats-pre-event-badge"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#6EE7B7]/30 bg-[#6EE7B7]/10 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6EE7B7]"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[#6EE7B7] animate-pulse" />
                  {tr ? 'Etkinlik öncesi · Kayıtlar açık' : 'Pre-event · Registrations open'}
                </span>
              </div>
              <h2 className="mt-3 font-playfair text-3xl md:text-4xl lg:text-5xl text-white leading-tight font-medium">
                {tr ? 'Dünya zirveye doğru hareket ediyor.' : 'The world is moving toward the summit.'}
              </h2>
              <p
                data-testid="stats-pre-event-caption"
                className="mt-4 text-sm md:text-[15px] leading-relaxed text-white/65 max-w-xl"
              >
                {tr
                  ? 'Sayılar gerçek başvurulardan canlı olarak gelir. Zirve yaklaştıkça delege, ülke ve tedarikçi rakamları büyümeye devam edecek.'
                  : 'Numbers are pulled live from real registrations. Delegate, country and supplier counts will continue to grow as the summit approaches.'}
              </p>

              {(() => {
                const ts  = stats?.last_registered_at;
                const ccs = Array.isArray(stats?.recent_country_codes) ? stats.recent_country_codes : [];
                const rel = relativeTime(ts, tr);
                if (!statsLoading && !statsError && ts && ccs.length > 0 && rel) {
                  return (
                    <div
                      data-testid="stats-recent-activity"
                      className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[12px] text-white/70"
                    >
                      <span className="font-semibold uppercase tracking-[0.18em] text-white/55 text-[10px]">
                        {tr ? 'Son kayıt' : 'Most recent'}
                      </span>
                      <span className="flex items-center gap-1 text-base leading-none">
                        {ccs.slice(0, 5).map(cc => {
                          const flag = ccToFlag(cc);
                          return flag ? (
                            <span key={cc} title={cc} aria-label={cc}>{flag}</span>
                          ) : null;
                        })}
                      </span>
                      <span className="text-white/30">·</span>
                      <span className="tabular-nums text-white/80">{rel}</span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
            {[
              {
                icon: Users, key: 'delegates',
                value: stats?.delegates_count,
                label: tr ? 'Kayıtlı Delege' : 'Registered Delegates',
                testid: 'stats-delegates',
              },
              {
                icon: Flag, key: 'countries',
                value: stats?.countries_count,
                label: tr ? 'Katılımcı Ülke' : 'Participating Countries',
                testid: 'stats-countries',
              },
              {
                icon: Handshake, key: 'suppliers',
                value: stats?.suppliers_count,
                label: tr ? 'Onaylı Tedarikçi' : 'Approved Suppliers',
                testid: 'stats-suppliers',
              },
            ].map((item, idx) => (
              <Reveal key={item.key} delay={idx * 120}>
                <div className="px-0 md:px-10 py-8 md:py-0 flex flex-col gap-3">
                  <item.icon className="h-6 w-6 text-[#6EE7B7]" strokeWidth={1.4} />
                  <span
                    data-testid={item.testid}
                    className="font-playfair text-5xl md:text-6xl lg:text-7xl text-white tabular-nums font-medium leading-none"
                  >
                    {statsLoading ? '—' : (item.value ?? 0).toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-[0.25em] font-semibold text-white/70">
                      {item.label}
                    </span>
                    {!statsLoading && !statsError && (item.value ?? 0) === 0 && (
                      <span
                        data-testid={`${item.testid}-soon`}
                        className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] font-semibold text-white/55"
                      >
                        {tr ? 'Yakında' : 'Coming soon'}
                      </span>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════════════════════ ABOUT + ZONE COMPARE ═════════════════════════ */}
      <section id="about" data-testid="about-section" className="py-20 md:py-32 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <Reveal className="lg:col-span-7">
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A94D32]">
              {tr ? 'SummitFlow Nedir?' : 'What is SummitFlow?'}
            </span>
            <h2 className="mt-3 font-playfair text-3xl md:text-5xl text-[#0A3A5A] font-medium leading-tight">
              {tr ? 'Liderleri, kurumları ve fikirleri buluşturan platform.' : 'The platform where leaders, institutions and ideas converge.'}
            </h2>
            <div className="mt-6 space-y-4 text-[#4A5568] text-base md:text-lg leading-relaxed max-w-2xl">
              <p>
                {tr
                  ? 'SummitFlow, küresel bir zirveyi baştan sona yöneten çok kiracılı bir platformdur: delege kaydı, katılımcı akreditasyonu, tedarikçi pazarı ve stand tasarım ihaleleri tek bir güvenli portalda birleşir.'
                  : 'SummitFlow is a multi-tenant platform that runs a global summit end-to-end: delegate registration, attendee accreditation, a supplier marketplace and stand-design tenders all come together in one secure portal.'}
              </p>
              <p>
                {tr
                  ? 'Güçlü doğrulama akışları, rol bazlı erişim ve gerçek zamanlı mesajlaşma ile katılımcılar, iş dünyası ve organizatörler aynı çatı altında sorunsuzca buluşur.'
                  : 'With strict verification flows, role-based access and real-time messaging, attendees, businesses and organisers come together seamlessly under one roof.'}
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#zones" className="inline-flex items-center gap-2 px-5 py-2.5 border border-[#0A3A5A] text-[#0A3A5A] rounded-md text-sm font-semibold hover:bg-[#0A3A5A] hover:text-white transition-all">
                {tr ? 'Katılım yolları' : 'Ways to participate'}
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <Link to="/about/faq" className="inline-flex items-center gap-2 px-5 py-2.5 text-[#0A3A5A] rounded-md text-sm font-semibold hover:underline">
                {tr ? 'Sık sorulan sorular' : 'Frequently asked questions'}
              </Link>
            </div>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-5">
            <div data-testid="zone-comparison-card" className="rounded-2xl bg-white border border-[rgba(10,58,90,0.10)] p-7 md:p-8 shadow-[0_20px_50px_-30px_rgba(10,58,90,0.25)]">
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A5568]">
                {tr ? 'Hızlı Karşılaştırma' : 'Quick Comparison'}
              </span>
              <h3 className="mt-2 font-playfair text-2xl text-[#0A3A5A] font-medium">
                {tr ? 'Delege vs Katılımcı' : 'Delegate vs Attendee'}
              </h3>
              <div className="mt-6 space-y-5">
                {[
                  [
                    tr ? 'Erişim' : 'Access',
                    tr ? 'Akredite delege' : 'Accredited delegate',
                    tr ? 'Açık başvuru' : 'Open application',
                  ],
                  [
                    tr ? 'İçerik' : 'Programme',
                    tr ? 'Resmi oturumlar' : 'Official sessions',
                    tr ? 'Paneller, sergiler' : 'Panels, expos',
                  ],
                  [
                    tr ? 'Katılımcı' : 'Audience',
                    tr ? 'Delegeler, ortaklar' : 'Delegates, partners',
                    tr ? 'İş dünyası, medya' : 'Business, media',
                  ],
                ].map(([row, blue, green]) => (
                  <div key={row} className="grid grid-cols-3 gap-4 text-sm">
                    <span className="text-[#4A5568] font-semibold">{row}</span>
                    <span className="flex items-start gap-2 text-[#0A3A5A]">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#0A3A5A]" />
                      {blue}
                    </span>
                    <span className="flex items-start gap-2 text-[#216E4E]">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#216E4E]" />
                      {green}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ AGENDA + EXPERIENCE ═════════════════════════ */}
      <section id="agenda" className="py-20 md:py-28 bg-gradient-to-b from-[#FAF9F6] to-white scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
          {/* Agenda timeline */}
          <Reveal className="lg:col-span-7">
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A94D32]">
              {tr ? 'Program' : 'Agenda'}
            </span>
            <h2 className="mt-3 font-playfair text-3xl md:text-4xl text-[#0A3A5A] font-medium leading-tight">
              {tr ? 'Üç gün. Tek bir vizyon.' : 'Three days. One shared vision.'}
            </h2>

            <ol data-testid="agenda-timeline" className="mt-10 relative border-l-2 border-[rgba(10,58,90,0.15)] pl-6 space-y-8">
              {[
                [tr ? '14 Ekim' : 'Oct 14', tr ? 'Açılış Töreni' : 'Opening Ceremony', tr ? 'Ev sahibi başkanlıktan açılış konuşmaları.' : 'Inaugural addresses by the host presidency.'],
                [tr ? '14 Ekim' : 'Oct 14', tr ? 'Ana Konuşmalar' : 'Keynote Sessions', tr ? 'Küresel liderlerden vizyon konuşmaları.' : 'Vision talks from global leaders.'],
                [tr ? '15 Ekim' : 'Oct 15', tr ? 'İnovasyon & Teknoloji' : 'Innovation & Technology', tr ? 'Yeni teknolojiler, girişimler ve demolar.' : 'New technologies, startups and demos.'],
                [tr ? '15 Ekim' : 'Oct 15', tr ? 'Networking & Anlaşmalar' : 'Networking & Deals', tr ? 'B2B görüşmeleri ve iş ortaklıkları.' : 'B2B meetings and partnerships.'],
                [tr ? '16 Ekim' : 'Oct 16', tr ? 'Kapanış Oturumu' : 'Closing Plenary', tr ? 'Sonuç bildirgesi ve kapanış.' : 'Outcome statement and closing.'],
              ].map(([date, title, desc], i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[34px] top-1.5 h-4 w-4 rounded-full bg-white border-2 border-[#216E4E]" />
                  <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A94D32]">{date}</span>
                  <h4 className="mt-1 font-playfair text-xl text-[#0A3A5A] leading-tight">{title}</h4>
                  <p className="mt-1 text-[#4A5568] text-sm md:text-[15px] max-w-xl">{desc}</p>
                </li>
              ))}
            </ol>
          </Reveal>

          {/* Experience card */}
          <Reveal delay={120} className="lg:col-span-5">
            <div
              data-testid="experience-card"
              className="relative block rounded-2xl overflow-hidden h-full min-h-[420px] group"
            >
              <img
                src="https://images.pexels.com/photos/20733081/pexels-photo-20733081.jpeg"
                alt="Summit experience"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-8 md:p-10 text-white">
                <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20">
                  <Mic2 className="h-3.5 w-3.5" />
                  <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">
                    {tr ? 'Deneyim' : 'Experience'}
                  </span>
                </div>
                <div>
                  <h3 className="font-playfair text-3xl md:text-4xl leading-tight">
                    {tr ? 'Sadece bir konferans değil.' : 'More than a conference.'}
                  </h3>
                  <p className="mt-3 text-white/85 text-sm md:text-base max-w-sm">
                    {tr
                      ? 'İlham veren konuşmalar, üst düzey networking oturumları ve unutulmaz bir zirve atmosferi.'
                      : 'Inspiring talks, high-level networking sessions and an unforgettable summit atmosphere.'}
                  </p>
                  <a href="#zones" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold group-hover:gap-3 transition-all">
                    {tr ? 'Katılım yollarını gör' : 'See how to join'}
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ PARTNERS ═════════════════════════ */}
      <section data-testid="partners-strip" className="py-16 md:py-20 border-y border-[rgba(10,58,90,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="text-center mb-10">
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A5568]">
                {tr ? 'Ortaklar ve Sponsorlar' : 'Partners & Sponsors'}
              </span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 items-center">
              {[
                { name: 'Platinum Partner', sub: tr ? 'Ana Sponsor' : 'Lead Sponsor' },
                { name: 'Gold Partner',     sub: tr ? 'Sponsor' : 'Sponsor' },
                { name: 'Media Partner',    sub: tr ? 'Medya Ortağı' : 'Media Partner' },
                { name: 'Host City',        sub: tr ? 'Ev Sahibi Şehir' : 'Host City' },
              ].map(p => (
                <div key={p.name} className="flex flex-col items-center text-center opacity-70 hover:opacity-100 transition-opacity">
                  <div className="h-16 w-full flex items-center justify-center">
                    <span className="font-playfair text-xl md:text-2xl text-[#0A3A5A]">{p.name}</span>
                  </div>
                  <span className="mt-2 text-[10px] uppercase tracking-[0.25em] font-semibold text-[#4A5568]">{p.sub}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ FAQ ═════════════════════════ */}
      <section id="faq" className="py-20 md:py-28 scroll-mt-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-end justify-between gap-6 mb-10 md:mb-14">
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#A94D32]">
                  {tr ? 'Sık Sorulan Sorular' : 'FAQ'}
                </span>
                <h2 className="mt-3 font-playfair text-3xl md:text-5xl text-[#0A3A5A] font-medium leading-tight">
                  {tr ? 'Kısaca yanıtlar.' : 'Quick answers.'}
                </h2>
              </div>
              <Link to="/about/faq" className="hidden md:inline-flex items-center gap-2 text-sm text-[#0A3A5A] font-semibold hover:underline">
                {tr ? 'Tüm sorular' : 'See all'}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div data-testid="faq-accordion" className="border-t border-[rgba(10,58,90,0.12)]">
              {faqs.map((f, i) => (
                <FaqRow
                  key={i}
                  q={f.q}
                  a={f.a}
                  open={faqOpen === i}
                  onToggle={() => setFaqOpen(faqOpen === i ? -1 : i)}
                  testId={`faq-${i}`}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ FOOTER ═════════════════════════ */}
      <footer data-testid="footer" className="relative bg-[#072840] text-white overflow-hidden">
        {/* Watermark */}
        <div className="pointer-events-none absolute -bottom-10 -right-6 font-playfair text-[12rem] md:text-[18rem] leading-none text-white/[0.04] whitespace-nowrap select-none">
          SummitFlow
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center">
                <Mountain className="h-5 w-5 text-[#6EE7B7]" />
              </div>
              <span className="font-playfair text-2xl">SummitFlow</span>
            </div>
            <p className="text-white/70 text-sm md:text-base max-w-sm leading-relaxed">
              {tr
                ? 'Küresel Liderlik ve İnovasyon Zirvesi — 14–16 Ekim 2026, İstanbul, Türkiye. Delegeler, iş dünyası ve tedarikçiler için tek platform.'
                : 'The Global Leadership & Innovation Summit — 14–16 October 2026, Istanbul, Türkiye. One platform for delegates, business and suppliers.'}
            </p>
          </div>

          {/* Links */}
          <div data-testid="footer-links" className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8 text-sm">
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.25em] font-semibold text-white/60 mb-4">
                {tr ? 'Kayıt' : 'Register'}
              </h4>
              <ul className="space-y-3">
                <li><span className="text-white/45 cursor-not-allowed">Delegate Access <span className="text-[9px] uppercase tracking-wider">· {tr ? 'Yakında' : 'Soon'}</span></span></li>
                <li><span className="text-white/45 cursor-not-allowed">Attendee Access <span className="text-[9px] uppercase tracking-wider">· {tr ? 'Yakında' : 'Soon'}</span></span></li>
                <li><Link to={localizedPath('supplierRegister')}   className="text-white/80 hover:text-white transition-colors">{tr ? 'Tedarikçi' : 'Supplier'}</Link></li>
                <li><Link to={localizedPath('contractorRegister')} className="text-white/80 hover:text-white transition-colors">{tr ? 'Yüklenici' : 'Contractor'}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.25em] font-semibold text-white/60 mb-4">
                {tr ? 'Keşfet' : 'Explore'}
              </h4>
              <ul className="space-y-3">
                <li><a href="#about"  className="text-white/80 hover:text-white transition-colors">{tr ? 'SummitFlow Nedir?' : 'What is SummitFlow?'}</a></li>
                <li><a href="#zones"  className="text-white/80 hover:text-white transition-colors">{tr ? 'Katılım Yolları' : 'Ways to Participate'}</a></li>
                <li><a href="#agenda" className="text-white/80 hover:text-white transition-colors">{tr ? 'Program' : 'Agenda'}</a></li>
                <li><Link to="/about/faq" className="text-white/80 hover:text-white transition-colors">{tr ? 'SSS' : 'FAQ'}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.25em] font-semibold text-white/60 mb-4">
                {tr ? 'İletişim' : 'Contact'}
              </h4>
              <ul className="space-y-3 text-white/80">
                <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5" /> hello@summitflow.net</li>
                <li className="flex items-start gap-2"><Clock className="h-4 w-4 mt-0.5" /> {tr ? 'İstanbul (UTC+3)' : 'Istanbul (UTC+3)'}</li>
                <li className="flex items-start gap-2"><LifeBuoy className="h-4 w-4 mt-0.5" />
                  <a href="https://summitflow.net" target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-1">
                    summitflow.net <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
            <span>© {new Date().getFullYear()} SummitFlow · {tr ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}</span>
            <span>{tr ? 'Bu portal yalnızca zirve kayıt ve yönetimi içindir.' : 'This portal serves summit registration and management only.'}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
